import { css, cx } from "@emotion/css";
import { borderRadius, fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import React from "react";
const badgeStyles = css({
    display: "inline-flex",
    borderRadius: borderRadius.circle,
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
});
const variantStyles: {
    [key in BadgeVariant]: string;
} = {
    danger: css({
        backgroundColor: themeTokens.color.badge.background.danger,
        color: themeTokens.color.badge.text.primary,
    }),
    neutral: css({
        backgroundColor: themeTokens.color.badge.background.neutral,
        color: themeTokens.color.badge.text.secondary,
    }),
    success: css({
        backgroundColor: themeTokens.color.badge.background.success,
        color: themeTokens.color.badge.text.primary,
    }),
    info: css({
        backgroundColor: themeTokens.color.badge.background.info,
        color: themeTokens.color.badge.text.primary,
    }),
    warning: css({
        backgroundColor: themeTokens.color.badge.background.warning,
        color: themeTokens.color.badge.text.primary,
    }),
    inverseNeutral: css({
        backgroundColor: themeTokens.color.badge.background.inverseNeutral,
        color: themeTokens.color.badge.text.primary,
    }),
    feature: css({
        backgroundColor: themeTokens.color.badge.background.feature,
        color: themeTokens.color.badge.text.feature,
    }),
};
type BadgeSize = BadgeProps["size"];
const sizeStyles: {
    [key in BadgeSize]: string;
} = {
    xSmall: css({
        height: "16px",
        minWidth: "16px",
        font: text.body.regular.xSmall,
        fontSize: "12px",
        padding: `0 ${space[4]}`,
    }),
    small: css({
        height: "20px",
        minWidth: "20px",
        font: text.body.regular.xSmall,
        padding: `0 ${space[6]}`,
        gap: space[4],
    }),
    medium: css({
        height: "24px",
        minWidth: "24px",
        font: text.body.regular.small,
        padding: `0 ${space[8]} 0 ${space[6]}`,
        gap: space[6],
    }),
};
export type BadgeVariant = "neutral" | "info" | "success" | "warning" | "danger" | "inverseNeutral" | "feature";
interface SharedBadgeProps {
    variant: BadgeVariant;
}
export interface SmallOrMediumBadgeProps extends SharedBadgeProps {
    size: "small" | "medium";
    count?: number;
    icon?: React.ReactNode;
    label?: string;
}
export interface ExtraSmallBadgeProps extends SharedBadgeProps {
    size: "xSmall";
    count?: number;
    label?: string;
}
export type BadgeProps = SmallOrMediumBadgeProps | ExtraSmallBadgeProps;
export function Badge(props: BadgeProps) {
    return props.size === "xSmall" ? (<ExtraSmallBadge size={props.size} count={props.count} label={props.label} variant={props.variant}/>) : (<SmallOrMediumBadge size={props.size} variant={props.variant} count={props.count} label={props.label} icon={props.icon}/>);
}
function ExtraSmallBadge(props: ExtraSmallBadgeProps) {
    return (<div className={cx(badgeStyles, variantStyles[props.variant], sizeStyles[props.size])}>
            <BadgeLabel label={props.label} count={props.count} size={props.size}/>
        </div>);
}
function SmallOrMediumBadge(props: SmallOrMediumBadgeProps) {
    return (<div className={cx(badgeStyles, variantStyles[props.variant], sizeStyles[props.size])}>
            {props.icon && <BadgeIcon size={props.size} icon={props.icon}/>}
            <BadgeLabel label={props.label} count={props.count} size={props.size}/>
        </div>);
}
const smallBadgeIconStyles = css({
    width: "16px",
    height: "16px",
});
const mediumBadgeIconStyles = css({
    width: "20px",
    height: "20px",
});
interface BadgeIconProps {
    icon: React.ReactNode;
    size: SmallOrMediumBadgeProps["size"];
}
function BadgeIcon({ icon, size }: BadgeIconProps) {
    return <div className={size === "medium" ? mediumBadgeIconStyles : smallBadgeIconStyles}>{icon}</div>;
}
interface BadgeLabelProps {
    label?: string;
    count?: number;
    size: "xSmall" | "small" | "medium";
}
const badgeLabelStyles = css({
    display: "flex",
    gap: space[4],
    justifyContent: "center",
    alignItems: "center",
});
const xSmallLabelStyles = css({
    fontWeight: fontWeight[600],
    fontSize: "12px",
    lineHeight: "16px",
});
function BadgeLabel({ label, count, size }: BadgeLabelProps) {
    return (<span className={cx(badgeLabelStyles, { [xSmallLabelStyles]: size === "xSmall" })}>
            {count !== undefined && <BadgeCount count={count} size={size}/>}
            {label}
        </span>);
}
const badgeCountStyles = css({
    display: "flex",
    justifyItems: "center",
    alignItems: "center",
});
const badgeCountSizeStyles: {
    [key in BadgeSize]: string;
} = {
    xSmall: css({
        fontWeight: fontWeight[600],
        fontSize: "12px",
        lineHeight: "16px",
    }),
    small: css({
        font: text.body.regular.xSmall,
    }),
    medium: css({
        font: text.body.bold.small,
    }),
};
function BadgeCount({ count, size }: {
    count: number;
    size: BadgeSize;
}) {
    return <span className={cx(badgeCountStyles, badgeCountSizeStyles[size])}>{count}</span>;
}
